import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useYacht } from "../../context/YachtContext";
import { api, fmtErr } from "../../lib/api";
import { PageHeader } from "./Dashboard";
import { Field, EmptyYacht } from "./Inventory";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import {
  Plus, Trash2, Play, ListChecks, Camera, CheckCircle2, Clock,
  ImagePlus, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { useRights } from "../../lib/useRights";
import { useTour } from "../../components/tour/TourContext";
import TourReplayButton from "../../components/tour/TourReplayButton";
import FileUploader, { AuthImage } from "../../components/FileUploader";
import ImageLightbox from "../../components/ImageLightbox";
import ConfirmDialog from "../../components/ConfirmDialog";

// Merged Type/Frequency picker — each option maps to a (kind, frequency)
// pair so the schema underneath stays unchanged. The label in the dropdown
// is the single source of truth for the crew, so they pick one thing
// instead of having to coordinate two related fields.
const TYPE_FREQUENCY = [
  { value: "departure:ad_hoc",         label: "Pre-departure (ad-hoc)",  kind: "departure",        frequency: "ad_hoc"   },
  { value: "arrival:ad_hoc",           label: "Post-arrival (ad-hoc)",   kind: "arrival",          frequency: "ad_hoc"   },
  { value: "custom:daily",             label: "Daily routine",           kind: "custom",           frequency: "daily"    },
  { value: "weekly_check:weekly",      label: "Weekly check",            kind: "weekly_check",     frequency: "weekly"   },
  { value: "weekly_safety:weekly",     label: "Weekly safety",           kind: "weekly_safety",    frequency: "weekly"   },
  { value: "biweekly_check:biweekly",  label: "Biweekly check",          kind: "biweekly_check",   frequency: "biweekly" },
  { value: "biweekly_safety:biweekly", label: "Biweekly safety",         kind: "biweekly_safety",  frequency: "biweekly" },
  { value: "monthly_check:monthly",    label: "Monthly check",           kind: "monthly_check",    frequency: "monthly"  },
  { value: "monthly_safety:monthly",   label: "Monthly safety",          kind: "monthly_safety",   frequency: "monthly"  },
  { value: "custom:ad_hoc",            label: "Custom (ad-hoc)",         kind: "custom",           frequency: "ad_hoc"   },
];

function typeFrequencyValue(kind, frequency) {
  // Match an existing option, else fall back to a custom+ad_hoc bucket so
  // a checklist created before this UI shipped still has a valid label.
  const exact = TYPE_FREQUENCY.find((o) => o.kind === kind && o.frequency === frequency);
  if (exact) return exact.value;
  const byKind = TYPE_FREQUENCY.find((o) => o.kind === kind);
  return byKind ? byKind.value : "custom:ad_hoc";
}

const DEFAULT_TEMPLATE = { name: "", kind: "custom", frequency: "ad_hoc", description: "", items: [] };

export default function Checklists() {
  const { t } = useTranslation();
  const { activeYacht } = useYacht();
  const { can } = useRights();
  const [templates, setTemplates] = useState([]);
  const [runs, setRuns] = useState([]);
  const [tab, setTab] = useState("templates"); // 'templates' | 'history'
  const [editor, setEditor] = useState(null);   // template being edited
  const [runId, setRunId] = useState(null);     // currently open run
  const [historyToDelete, setHistoryToDelete] = useState(null);
  const [histDelBusy, setHistDelBusy] = useState(false);
  // Click-anywhere-on-card → open detail modal (mirrors Tasks / Periodic
  // tasks pattern). The card itself no longer carries Run/Edit/Delete
  // buttons; those live inside this modal.
  const [detailTpl, setDetailTpl] = useState(null);
  // Confirmation gate before a delete fires — protects against accidental
  // taps. We track which template is pending deletion + a busy flag for
  // the inline button spinner.
  const [tplToDelete, setTplToDelete] = useState(null);
  const [tplDelBusy, setTplDelBusy] = useState(false);

  const loadTemplates = async () => {
    if (!activeYacht?.id) return;
    try {
      const { data } = await api.get(`/checklists?yacht_id=${activeYacht.id}`);
      setTemplates(data);
    } catch { setTemplates([]); }
  };
  const loadRuns = async () => {
    if (!activeYacht?.id) return;
    try {
      const { data } = await api.get(`/checklist-runs?yacht_id=${activeYacht.id}`);
      setRuns(data);
    } catch { setRuns([]); }
  };
  useEffect(() => {
    setTemplates([]); setRuns([]);
    setRunId(null); setEditor(null); setDetailTpl(null);
    loadTemplates(); loadRuns();
    /* eslint-disable-next-line */
  }, [activeYacht?.id]);

  // Allow the Dashboard "Checklists due" panel to deep-link straight into
  // a run via `/app/checklists?run=<chk_id>`. We wait until templates have
  // loaded, find the matching one, start the run, then strip the query so
  // a refresh doesn't restart it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const runChkId = searchParams.get("run");
    if (!runChkId || templates.length === 0) return;
    const tpl = templates.find((t) => t.id === runChkId);
    if (tpl) {
      startRun(tpl);
      const next = new URLSearchParams(searchParams);
      next.delete("run");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [templates, searchParams]);

  const startRun = async (tpl) => {
    try {
      const { data } = await api.post(`/checklists/${tpl.id}/runs`);
      const newRunId = data.id || data.existing;
      setRunId(newRunId);
      await loadRuns();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const seedDefaults = async () => {
    try {
      const { data } = await api.post(`/checklists/seed-defaults`, { yacht_id: activeYacht.id });
      const seededCount = (data.seeded || []).length;
      const backfilledCount = (data.backfilled || []).length;
      if (seededCount > 0 && backfilledCount > 0) {
        toast.success(`Seeded ${seededCount} new + updated ${backfilledCount} existing template${backfilledCount === 1 ? "" : "s"}.`);
      } else if (seededCount > 0) {
        toast.success(`Seeded ${seededCount} default checklist${seededCount === 1 ? "" : "s"}.`);
      } else if (backfilledCount > 0) {
        toast.success(`Updated ${backfilledCount} existing template${backfilledCount === 1 ? "" : "s"} with frequency labels.`);
      } else {
        toast("All default checklists already exist.");
      }
      loadTemplates();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  // `del` now opens the confirm dialog instead of using window.confirm
  // (window.confirm is suppressed inside the preview iframe and gives a
  // poor mobile UX anyway). The actual API call happens in the dialog's
  // onConfirm handler below.
  const requestDelete = (tpl) => setTplToDelete(tpl);

  const performDelete = async () => {
    if (!tplToDelete) return;
    setTplDelBusy(true);
    try {
      await api.delete(`/checklists/${tplToDelete.id}`);
      toast.success(`"${tplToDelete.name}" deleted. Past runs are kept.`);
      setTplToDelete(null);
      setDetailTpl(null);  // also close the detail modal if it was open
      loadTemplates();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setTplDelBusy(false);
    }
  };

  const activeRuns = runs.filter((r) => !r.completed_at);
  const pastRuns = runs.filter((r) => r.completed_at);

  if (!activeYacht) return <EmptyYacht />;

  return (
    <div className="p-6 lg:p-10 max-w-7xl">
      <PageHeader
        overline={activeYacht.name}
        title={t("checklists.title")}
        right={
          can("checklist:create") && (
            <div className="flex flex-row flex-wrap items-center gap-2">
              <TourReplayButton tourId="checklist" />
              <Button variant="outline" onClick={seedDefaults} data-testid="seed-defaults-btn">
                Seed defaults
              </Button>
              <Button
                className="bg-ink hover:bg-black"
                onClick={() => setEditor({ ...DEFAULT_TEMPLATE })}
                data-testid="new-checklist-btn"
              >
                <Plus className="h-4 w-4 mr-2" />{t("checklists.new_checklist")}
              </Button>
            </div>
          )
        }
      />

      {/* Active runs strip */}
      {activeRuns.length > 0 && (
        <div className="mt-6 space-y-2" data-testid="active-runs">
          <div className="text-[11px] uppercase tracking-wider font-medium text-zinc-700 flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" /> Active runs
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {activeRuns.map((r) => {
              const done = r.items.filter((i) => i.done).length;
              const total = r.items.length;
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setRunId(r.id)}
                  className="text-left rounded-md border border-emerald-300 bg-emerald-50/40 p-3 hover:border-emerald-500 transition-colors"
                  data-testid={`active-run-${r.id}`}
                >
                  <div className="text-sm font-medium">{r.name_snapshot}</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">
                    started by {r.started_by_name} · {fmtRel(r.started_at)}
                  </div>
                  <div className="mt-2 text-[12px] text-zinc-700">
                    {done}/{total} done
                    <div className="mt-1 h-1.5 rounded-full bg-zinc-200 overflow-hidden">
                      <div className="h-full bg-emerald-500" style={{ width: `${(done / total) * 100}%` }} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-8 flex items-center gap-1 border-b border-zinc-200">
        <TabBtn active={tab === "templates"} onClick={() => setTab("templates")} testid="tab-templates">
          Templates ({templates.length})
        </TabBtn>
        <TabBtn active={tab === "history"} onClick={() => setTab("history")} testid="tab-history">
          History ({pastRuns.length})
        </TabBtn>
      </div>

      {tab === "templates" ? (
        <TemplatesGrid
          templates={templates}
          can={can}
          onOpenDetail={setDetailTpl}
          onSeed={seedDefaults}
        />
      ) : (
        <HistoryList
          runs={pastRuns}
          onOpen={setRunId}
          canDelete={can("checklist:run_delete")}
          onDelete={(r) => setHistoryToDelete(r)}
        />
      )}

      {detailTpl && (
        <TemplateDetailModal
          template={detailTpl}
          can={can}
          onClose={() => setDetailTpl(null)}
          onRun={async () => { const t = detailTpl; setDetailTpl(null); await startRun(t); }}
          onEdit={() => { const t = detailTpl; setDetailTpl(null); setEditor(t); }}
          onDeleteRequest={() => requestDelete(detailTpl)}
        />
      )}

      <ConfirmDialog
        open={!!tplToDelete}
        title="Delete checklist template?"
        description={tplToDelete ? `"${tplToDelete.name}" will be removed. Past runs of this checklist are kept in History and are not affected.` : ""}
        confirmLabel="Yes, delete"
        destructive
        busy={tplDelBusy}
        onCancel={() => { if (!tplDelBusy) setTplToDelete(null); }}
        onConfirm={performDelete}
      />

      {editor && (
        <TemplateEditor
          template={editor}
          yacht_id={activeYacht.id}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); loadTemplates(); }}
        />
      )}

      {runId && (
        <RunModal
          runId={runId}
          can={can}
          onClose={() => { setRunId(null); loadRuns(); }}
        />
      )}

      <ConfirmDialog
        open={!!historyToDelete}
        title="Delete checklist run?"
        description={historyToDelete ? `"${historyToDelete.name_snapshot}" will be removed from history. This cannot be undone.` : ""}
        confirmLabel="Yes, delete"
        destructive
        busy={histDelBusy}
        onCancel={() => { if (!histDelBusy) setHistoryToDelete(null); }}
        onConfirm={async () => {
          if (!historyToDelete) return;
          setHistDelBusy(true);
          try {
            await api.delete(`/checklist-runs/${historyToDelete.id}`);
            toast.success("History entry deleted");
            setHistoryToDelete(null);
            loadRuns();
          } catch (e) {
            toast.error(fmtErr(e));
          } finally {
            setHistDelBusy(false);
          }
        }}
        testid="checklist-history-confirm"
      />
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`relative px-4 py-2 text-sm transition-colors ${
        active ? "text-ink" : "text-zinc-500 hover:text-zinc-700"
      }`}
    >
      {children}
      {active && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-ink" />}
    </button>
  );
}

function TemplatesGrid({ templates, can, onOpenDetail, onSeed }) {
  if (templates.length === 0) {
    return (
      <div className="mt-10 rounded-md border border-zinc-200 bg-white p-10 text-center">
        <ListChecks className="h-7 w-7 mx-auto text-zinc-300" />
        <div className="mt-2 text-sm font-medium">No checklists yet.</div>
        <div className="mt-1 text-[12px] text-zinc-500">
          A good start is the bundled set — Pre-departure, Post-arrival, Weekly safety.
        </div>
        {can("checklist:create") && (
          <Button className="mt-4 bg-ink hover:bg-black" onClick={onSeed} data-testid="seed-empty-btn">
            Seed defaults
          </Button>
        )}
      </div>
    );
  }
  return (
    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {templates.map((t) => (
        <div
          key={t.id}
          className="rounded-md border border-zinc-200 bg-white p-4 cursor-pointer hover:border-zinc-300 active:bg-zinc-50 transition-colors"
          data-testid={`tpl-${t.id}`}
          onClick={() => onOpenDetail(t)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenDetail(t); } }}
        >
          <div className="flex items-start justify-between">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
                {TYPE_FREQUENCY.find((o) => o.kind === t.kind && o.frequency === (t.frequency || "ad_hoc"))?.label
                  || TYPE_FREQUENCY.find((o) => o.kind === t.kind)?.label
                  || t.kind}
                {t.frequency && t.frequency !== "ad_hoc" && (
                  <span
                    className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-medium ${
                      t.frequency === "daily" ? "bg-sky-50 text-sky-700"
                      : t.frequency === "weekly" ? "bg-violet-50 text-violet-700"
                      : t.frequency === "biweekly" ? "bg-purple-50 text-purple-700"
                      : t.frequency === "monthly" ? "bg-fuchsia-50 text-fuchsia-700"
                      : "bg-purple-50 text-purple-700"
                    }`}
                    data-testid={`tpl-frequency-pill-${t.id}`}
                  >
                    {t.frequency}
                  </span>
                )}
              </div>
              <div className="text-sm font-medium mt-0.5 break-words">{t.name}</div>
              <div className="text-[11px] text-zinc-500 mt-0.5">{t.items.length} items</div>
            </div>
          </div>
          {t.description && (
            <div className="text-[12px] text-zinc-600 mt-2 line-clamp-2">{t.description}</div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ─── Template detail modal ───────────────────────────────────────────
 * Read-only summary of a checklist template + the three actions the
 * user can take on it: Run (kick off a new in-progress run), Edit,
 * Delete (with confirmation). The card on the grid is just a teaser
 * that opens this; same UX pattern as Tasks and Periodic tasks.
 */
function TemplateDetailModal({ template, can, onClose, onRun, onEdit, onDeleteRequest }) {
  const t = template;
  const typeLabel =
    TYPE_FREQUENCY.find((o) => o.kind === t.kind && o.frequency === (t.frequency || "ad_hoc"))?.label
    || TYPE_FREQUENCY.find((o) => o.kind === t.kind)?.label
    || t.kind;
  const [running, setRunning] = useState(false);

  const handleRun = async () => {
    if (running) return;
    setRunning(true);
    try { await onRun(); }
    finally { setRunning(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="w-[calc(100vw-1rem)] sm:max-w-md max-h-[calc(100dvh-1rem)] overflow-y-auto p-4 sm:p-6"
        data-testid="tpl-detail-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg pr-6 break-words">{t.name}</DialogTitle>
          <DialogDescription className="text-[12px] sm:text-sm">
            Checklist template — starting a run creates a fresh, time-stamped copy in Active runs.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 text-sm mt-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider text-zinc-500">{typeLabel}</span>
            {t.frequency && t.frequency !== "ad_hoc" && (
              <span
                className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-medium ${
                  t.frequency === "daily" ? "bg-sky-50 text-sky-700"
                  : t.frequency === "weekly" ? "bg-violet-50 text-violet-700"
                  : t.frequency === "biweekly" ? "bg-purple-50 text-purple-700"
                  : t.frequency === "monthly" ? "bg-fuchsia-50 text-fuchsia-700"
                  : "bg-purple-50 text-purple-700"
                }`}
              >
                {t.frequency}
              </span>
            )}
            <span className="text-[11px] text-zinc-500">· {t.items.length} item{t.items.length === 1 ? "" : "s"}</span>
          </div>
          {t.description && (
            <div className="text-[13px] text-zinc-700 whitespace-pre-wrap">{t.description}</div>
          )}
        </div>

        <div className="mt-3 rounded-md border border-zinc-200 bg-zinc-50/60 max-h-56 overflow-y-auto">
          <ol className="divide-y divide-zinc-200">
            {t.items.map((it, i) => {
              // Item shape changed over time — legacy entries are plain
              // strings, current entries are { id, label, photo_required }.
              // Render whichever is present.
              const label = typeof it === "string" ? it : (it.label || it.text || "");
              return (
                <li key={i} className="flex items-start gap-2 px-3 py-2 text-[13px]">
                  <span className="text-zinc-400 font-mono text-[11px] mt-0.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <span className="flex-1 break-words">{label}</span>
                  {typeof it === "object" && it.photo_required && (
                    <Camera className="h-3.5 w-3.5 text-zinc-400 shrink-0 mt-0.5" title="Photo required" />
                  )}
                </li>
              );
            })}
            {t.items.length === 0 && (
              <li className="px-3 py-3 text-[12px] text-zinc-500">No items yet — edit to add some.</li>
            )}
          </ol>
        </div>

        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2 mt-3">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">Close</Button>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            {can("checklist:delete") && (
              <Button
                variant="outline"
                className="w-full sm:w-auto text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                onClick={onDeleteRequest}
                data-testid="tpl-detail-delete"
              >
                <Trash2 className="h-4 w-4 mr-2" />Delete
              </Button>
            )}
            {can("checklist:edit") && (
              <Button
                variant="outline"
                onClick={onEdit}
                className="w-full sm:w-auto"
                data-testid="tpl-detail-edit"
              >
                Edit
              </Button>
            )}
            {can("checklist:run_start") && (
              <Button
                onClick={handleRun}
                disabled={running}
                className="w-full sm:w-auto bg-ink hover:bg-black"
                data-testid="tpl-detail-run"
              >
                <Play className="h-4 w-4 mr-2" />{running ? "Starting…" : "Run"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



function HistoryList({ runs, onOpen, canDelete, onDelete }) {
  if (runs.length === 0) {
    return (
      <div className="mt-10 rounded-md border border-zinc-200 bg-white p-10 text-center">
        <Clock className="h-6 w-6 mx-auto text-zinc-300" />
        <div className="mt-2 text-sm text-zinc-700">No completed runs yet.</div>
      </div>
    );
  }
  return (
    <ul className="mt-6 divide-y divide-zinc-200 rounded-md border border-zinc-200 bg-white">
      {runs.map((r) => {
        const done = r.items.filter((i) => i.done).length;
        return (
          <li key={r.id} className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onOpen(r.id)}
              className="flex-1 min-w-0 text-left px-4 py-3 hover:bg-zinc-50 flex items-center justify-between gap-3"
              data-testid={`hist-run-${r.id}`}
            >
              <div className="min-w-0">
                <div className="text-sm font-medium">{r.name_snapshot}</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  Started by {r.started_by_name} · {fmtRel(r.started_at)} → completed {fmtRel(r.completed_at)}
                </div>
              </div>
              <div className="text-[11px] text-zinc-500 shrink-0">
                {done}/{r.items.length} done · <span className="text-emerald-700">complete</span>
              </div>
            </button>
            {canDelete && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); onDelete(r); }}
                className="mr-2 text-zinc-400 hover:text-red-600"
                title="Delete this history entry"
                data-testid={`hist-delete-${r.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function TemplateEditor({ template, yacht_id, onClose, onSaved }) {
  const [form, setForm] = useState(template);
  const [newItem, setNewItem] = useState("");
  const { activeTour } = useTour();
  const isEdit = !!template.id;

  const addItem = () => {
    if (!newItem.trim()) return;
    setForm({ ...form, items: [...form.items, { label: newItem.trim(), photo_required: false }] });
    setNewItem("");
  };
  const updateItem = (i, patch) => {
    const items = form.items.slice();
    items[i] = { ...items[i], ...patch };
    setForm({ ...form, items });
  };
  const removeItem = (i) => {
    setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  };

  const save = async () => {
    try {
      const payload = { ...form, yacht_id };
      if (isEdit) await api.patch(`/checklists/${template.id}`, payload);
      else await api.post(`/checklists`, payload);
      toast.success(`Checklist ${isEdit ? "saved" : "created"}`);
      onSaved();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-xl max-h-[90vh] overflow-y-auto"
        data-testid="template-editor"
        onPointerDownOutside={(e) => { if (activeTour) e.preventDefault(); }}
        onInteractOutside={(e) => { if (activeTour) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (activeTour) e.preventDefault(); }}
        onOpenAutoFocus={(e) => { if (activeTour) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit checklist" : "New checklist"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name *" className="col-span-2">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tpl-name" />
          </Field>
          <Field label="Type / Frequency">
            <select
              className="h-9 w-full rounded-md border border-input px-3 text-sm"
              value={typeFrequencyValue(form.kind, form.frequency)}
              onChange={(e) => {
                const opt = TYPE_FREQUENCY.find((o) => o.value === e.target.value);
                if (opt) setForm({ ...form, kind: opt.kind, frequency: opt.frequency });
              }}
              data-testid="tpl-type-frequency"
            >
              {TYPE_FREQUENCY.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Description" className="col-span-2">
            <Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>
        </div>
        <div className="mt-3 rounded-md border border-zinc-200 bg-zinc-50/40 p-3">
          <div className="text-[11px] uppercase tracking-wider font-medium text-zinc-600 mb-2">Items ({form.items.length})</div>
          {form.items.length === 0 && (
            <div className="text-[12px] text-zinc-500 mb-2">No items yet. Add the first one below.</div>
          )}
          <ul className="space-y-1.5">
            {form.items.map((it, i) => (
              <li key={i} className="flex items-center gap-2" data-testid={`tpl-item-${i}`}>
                <Input
                  value={it.label}
                  onChange={(e) => updateItem(i, { label: e.target.value })}
                  className="flex-1"
                />
                <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(i)} className="text-red-600">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
          <div className="mt-2 flex gap-2">
            <Input
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              placeholder="Add an item — e.g. Fenders retracted"
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(); }}}
              data-testid="new-item-input"
            />
            <Button type="button" className="bg-ink hover:bg-black" onClick={addItem} data-testid="add-item-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="bg-ink hover:bg-black" disabled={!form.name || form.items.length === 0} data-testid="save-tpl">
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RunModal({ runId, can, onClose }) {
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  // Lightbox cursor — paths is the rolling list of photos present in the
  // run (rebuilt every render so completed/replaced photos stay in sync).
  const [lightbox, setLightbox] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/checklist-runs/${runId}`);
      setRun(data);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [runId]);

  const tick = async (item, done, photo_path = null) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/checklist-runs/${runId}/items/${item.id}`, { done, photo_path });
      setRun(data);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  // Build the photo carousel target list once per render, so clicking any
  // thumbnail opens the lightbox positioned at that photo and the user can
  // arrow-key through every other photo in this run without closing it.
  const runPhotos = (run?.items || [])
    .filter((it) => !!it.photo_path)
    .map((it) => it.photo_path);
  const openLightboxAt = (path) => {
    const idx = runPhotos.indexOf(path);
    if (idx < 0) return;
    setLightbox({ paths: runPhotos, index: idx });
  };

  const complete = async () => {
    try {
      const { data } = await api.post(`/checklist-runs/${runId}/complete`);
      setRun(data);
      toast.success("Checklist complete — all clear!");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (!run) {
    return (
      <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="max-w-2xl"><div className="p-6 text-sm text-zinc-500">Loading…</div></DialogContent>
      </Dialog>
    );
  }

  const done = run.items.filter((i) => i.done).length;
  const total = run.items.length;
  const allDone = done === total;
  const completed = !!run.completed_at;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="run-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {run.name_snapshot}
            {completed && (
              <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                <CheckCircle2 className="h-3 w-3" /> Completed
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="text-[11px] text-zinc-500">
          Started by <span className="font-medium text-zinc-700">{run.started_by_name}</span> · {fmtRel(run.started_at)}
        </div>
        <div className="mt-2 mb-3">
          <div className="text-[11px] text-zinc-600 mb-1">{done}/{total} done</div>
          <div className="h-1.5 rounded-full bg-zinc-200 overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${(done / total) * 100}%` }} />
          </div>
        </div>
        <ul className="divide-y divide-zinc-200 rounded-md border border-zinc-200 bg-white">
          {run.items.map((it) => (
            <RunItemRow
              key={it.id}
              item={it}
              completed={completed}
              canTick={can("checklist:run_tick") && !completed}
              yacht_id={run.yacht_id}
              onTick={(done, photo_path) => tick(it, done, photo_path)}
              onOpenPhoto={openLightboxAt}
            />
          ))}
        </ul>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {!completed && can("checklist:run_tick") && (
            <Button
              onClick={complete}
              className={allDone ? "bg-emerald-600 hover:bg-emerald-700" : "bg-zinc-300 cursor-not-allowed"}
              disabled={!allDone}
              data-testid="complete-run-btn"
            >
              <CheckCircle2 className="h-4 w-4 mr-2" /> All clear — complete
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
      <ImageLightbox state={lightbox} onClose={() => setLightbox(null)} />
    </Dialog>
  );
}

function RunItemRow({ item, completed, canTick, yacht_id, onTick, onOpenPhoto }) {
  // Photos are always optional — any crew member running the checklist can
  // attach a snapshot to any item if they want a visual record (e.g.
  // "engine room inspection clear"). Never blocks the tick.
  return (
    <li className="px-4 py-3 flex items-start gap-3" data-testid={`run-item-${item.id}`}>
      <div className="pt-0.5">
        <Checkbox
          checked={item.done}
          onCheckedChange={(v) => { if (canTick) onTick(!!v); }}
          disabled={!canTick}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm ${item.done ? "line-through text-zinc-400" : "text-zinc-800"}`}>
          {item.label}
        </div>
        {item.done && item.done_by_name && (
          <div className="text-[11px] text-zinc-500 mt-0.5">
            by {item.done_by_name} · {fmtRel(item.done_at)}
          </div>
        )}
        {item.photo_path && (
          <div className="mt-2">
            <button
              type="button"
              onClick={() => onOpenPhoto?.(item.photo_path)}
              className="block w-24 h-24 overflow-hidden rounded border border-zinc-200 hover:border-zinc-400 transition-colors focus:outline-none focus:ring-2 focus:ring-zinc-400"
              aria-label="Open photo"
              data-testid={`run-item-photo-${item.id}`}
            >
              <AuthImage path={item.photo_path} className="w-full h-full object-cover" />
            </button>
          </div>
        )}
      </div>
      {!completed && canTick && (
        <FileUploader
          yacht_id={yacht_id}
          folder="checklists/photos"
          accept="image/*"
          label={item.photo_path ? <><ImagePlus className="h-3.5 w-3.5 mr-1 inline" />Replace</> : <><Camera className="h-3.5 w-3.5 mr-1 inline" />Photo</>}
          testid={`run-photo-${item.id}`}
          onUploaded={(path) => onTick(item.done, path)}
        />
      )}
    </li>
  );
}

function fmtRel(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.round(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}
